import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Badge } from "@/shared/ui/badge";
import { Package } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { ClientAsset } from "@/features/assets/hooks/useClientAssets";

interface ClientAssetsTableProps {
  assets: ClientAsset[];
  loading: boolean;
  onAssetClick: (assetId: string, propertyId: string) => void;
  selectedAssetId?: string;
}

export function ClientAssetsTable({ assets, loading, onAssetClick, selectedAssetId }: ClientAssetsTableProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="min-w-[200px]">Asset Name</TableHead>
                <TableHead className="min-w-[120px]">Category</TableHead>
                <TableHead className="min-w-[140px]">Manufacturer</TableHead>
                <TableHead className="min-w-[120px]">Condition</TableHead>
                <TableHead className="min-w-[100px]">Age</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-6 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-6 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                  <TableCell><div className="h-6 w-16 bg-muted animate-pulse rounded-full" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="rounded-lg border border-border/50 bg-muted/20">
        <div className="p-12 text-center">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No assets found</h3>
          <p className="text-sm text-muted-foreground">
            Try adjusting your filters or search criteria
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="min-w-[200px]">Asset Name</TableHead>
              <TableHead className="min-w-[120px]">Category</TableHead>
              <TableHead className="min-w-[140px]">Model Number</TableHead>
              <TableHead className="min-w-[120px]">Condition</TableHead>
              <TableHead className="min-w-[100px]">Age</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.map((asset) => {
              const isSelected = selectedAssetId === asset.id;
              const condition = asset.condition_status ?? null;
              const ageYears = asset.estimated_age_years;

              return (
                <TableRow
                  key={asset.id}
                  className={cn(
                    "cursor-pointer transition-all duration-200",
                    "hover:bg-accent/50",
                    isSelected && "bg-primary/10 hover:bg-primary/15"
                  )}
                  onClick={() => onAssetClick(asset.id, asset.property_id)}
                >
                  <TableCell className="font-semibold">
                    {asset.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {asset.category || asset.asset_type || 'Uncategorized'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {asset.model_number || '-'}
                  </TableCell>
                  <TableCell>
                    {condition ? (
                      <Badge variant="outline" className="font-normal capitalize">
                        {condition}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {ageYears == null ? (
                      <span className="text-sm text-muted-foreground">-</span>
                    ) : ageYears === 0 ? (
                      <span className="text-sm text-muted-foreground">Unable to determine</span>
                    ) : (
                      <Badge variant="outline" className="font-normal">
                        {ageYears} yrs
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
